import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import liveBlogService, { ActiveLiveBlog } from '../../services/liveBlogService';
import ThumbnailImage from '../ThumbnailImage';
import { t } from '../../i18n';

/**
 * Horizontale Leiste mit Live-Blogs (Startseite).
 * Karte = Thumbnail des verknüpften News-Artikels + LIVE-Badge; Klick
 * öffnet den Artikel direkt im Live-Blog-Reiter (?tab=live).
 * Refresh alle 2 Minuten.
 *
 * PLATZIERUNGS-LOGIK (Marcel 2026-08-31): Läuft mindestens ein Blog LIVE,
 * sitzt die Section oben an der gewohnten Stelle ('top'). Sind nur noch
 * beendete Blogs da, wandert sie unter "Kommende Turniere"
 * ('below-upcoming'). Beide Instanzen liegen auf der HomePage — welche
 * rendert, entscheidet shouldShowLiveBlogRail.
 */
export type LiveBlogRailPlacement = 'top' | 'below-upcoming';

/** PURE: rendert diese Instanz bei der aktuellen Blog-Lage? */
export function shouldShowLiveBlogRail(
  placement: LiveBlogRailPlacement,
  blogs: { status?: string | null }[],
): boolean {
  if (blogs.length === 0) return false;
  const hasLive = blogs.some((b) => b.status === 'live');
  return placement === 'top' ? hasLive : !hasLive;
}

const LiveBlogRail: React.FC<{ placement?: LiveBlogRailPlacement }> = ({
  placement = 'top',
}) => {
  const [blogs, setBlogs] = useState<ActiveLiveBlog[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      liveBlogService
        .getActiveLiveBlogs()
        .then((list) => {
          // Nur Blogs mit verknüpftem Artikel — ohne Ziel-Link keine Karte
          if (!cancelled) setBlogs(list.filter((b) => b.article?.slug));
        })
        .finally(() => {
          if (!cancelled) setLoaded(true);
        });
    };
    load();
    const interval = window.setInterval(load, 120_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  // CLS-Fix (2026-09-01): solange die Blogs LADEN, hält die obere Instanz
  // den Platz mit einem Skeleton in Kartenhöhe (16:9, w-56/sm:w-64); erst wenn
  // feststeht, dass kein Blog live ist, kollabiert sie auf null.
  if (!loaded) {
    if (placement !== 'top') return null;
    return (
      <section aria-label={t('web.component.live-blog-rail.live-blogs')} className="mb-4" aria-hidden="true">
        <h2 className="section-heading mb-3 text-base font-bold text-white">{t('web.component.live-blog-rail.live-blogs')}</h2>
        <div className="-mx-4 flex gap-3 overflow-hidden px-4 pb-1 sm:mx-0 sm:px-0">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="aspect-video w-56 shrink-0 animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900 sm:w-64"
            />
          ))}
        </div>
      </section>
    );
  }

  if (!shouldShowLiveBlogRail(placement, blogs)) return null;

  return (
    <section aria-label={t('web.component.live-blog-rail.live-blogs')} className="mb-4">
      <h2 className="section-heading mb-3 text-base font-bold text-white">{t('web.component.live-blog-rail.live-blogs')}</h2>
      <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {blogs.map((blog) => {
          const image = liveBlogService.getArticleImageUrl(blog);
          const key = blog.id || blog._id || blog.article?.slug || '';
          const isLive = blog.status === 'live';
          return (
            <Link
              key={key}
              to={`/news/${blog.article!.slug}?tab=live`}
              className={`group relative aspect-video w-56 shrink-0 overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 transition-colors sm:w-64 ${
                isLive ? 'hover:border-red-500/60' : 'hover:border-[#3fced1]/50'
              }`}
            >
              {image ? (
                // Fix 16:9 (Marcel 2026-09-22, Web und App identisch): 640×360-Thumbnail –
                // das quadratische 400er schnitt im 16:9-Rahmen links/rechts große Teile ab
                <ThumbnailImage
                  src={image}
                  thumbSize="wide"
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : null}
              <div className="dc-on-image absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />

              {isLive ? (
                <span className="absolute left-2.5 top-2.5 flex items-center gap-1.5 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-extrabold tracking-wide text-white">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                  </span>
                  LIVE
                </span>
              ) : (
                <span className="dc-on-image absolute left-2.5 top-2.5 rounded-full bg-zinc-800/90 px-2 py-0.5 text-[10px] font-extrabold tracking-wide text-zinc-300">
                  {t('web.component.event-timeline-card.beendet')}
                </span>
              )}

              <div className="dc-on-image absolute inset-x-0 bottom-0 p-2.5">
                <p className="line-clamp-2 text-xs font-bold leading-snug text-white">
                  {blog.title || blog.article?.title}
                </p>
                {typeof blog.entryCount === 'number' && blog.entryCount > 0 && (
                  <p className="mt-0.5 text-[10px] text-zinc-300">
                    {blog.entryCount} {blog.entryCount === 1 ? t('web.component.live-blog-rail.update') : t('web.component.live-blog-rail.updates')}{' '}{t('web.component.live-blog-rail.live-blog')}
                  </p>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default LiveBlogRail;
